import { createBrowserRouter, Navigate } from 'react-router-dom';
import { LoginPage } from '../pages/login';
import { DashboardPage } from '../pages/dashboard';
import { SellersPage } from '../pages/sellers';
import { ProductsPage } from '../pages/products';
import { OrdersPage } from '../pages/orders';
import { DeliveryPage } from '../pages/delivery';
import { CustomersPage } from '../pages/customers';
import { ReviewsPage } from '../pages/reviews';
import { NotificationsPage } from '../pages/notifications';
import { SupportPage } from '../pages/support';
import { ReportsPage } from '../pages/reports';
import { AuditTrailPage } from '../pages/audit-trail';
import { ErrorLogsPage } from '../pages/error-logs';
import { CommunicationLogsPage } from '../pages/communication-logs';
import { IntegrationsPage } from '../pages/integrations';
import { SmsTemplatesPage } from '../pages/sms-templates';
import { AppContentPage } from '../pages/app-content';
import { MastersPage } from '../pages/masters';
import { CatalogMastersPage } from '../pages/catalog';
import { PayoutsPage } from '../pages/payouts';
import { RefundsPage } from '../pages/refunds';
import { AdminsPage } from '../pages/admins';
import { SettingsPage } from '../pages/settings';
import { TwoFactorSetupPage } from '../pages/2fa-setup';
import { AdminLayout } from './layout';
import { RequireAuth } from './require-auth';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AdminLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: <DashboardPage /> },
      { path: 'sellers', element: <SellersPage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'delivery', element: <DeliveryPage /> },
      { path: 'customers', element: <CustomersPage /> },
      { path: 'reviews', element: <ReviewsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'support', element: <SupportPage /> },
      { path: 'reports', element: <ReportsPage /> },
      {
        // Categories, brands, colours, banners, tax and commission — the
        // catalog-shaped masters. Gated on category read because that is the
        // tab the page opens on; the others gate their own controls.
        path: 'catalog',
        element: (
          <RequireAuth scopes={['catalog.category.read']}>
            <CatalogMastersPage />
          </RequireAuth>
        ),
      },
      {
        // Every other admin-managed vocabulary: vehicle types, KYC document
        // types, rejection reasons, support categories…
        path: 'masters',
        element: (
          <RequireAuth scopes={['master.read']}>
            <MastersPage />
          </RequireAuth>
        ),
      },
      {
        path: 'payouts',
        element: (
          <RequireAuth scopes={['payout.read']}>
            <PayoutsPage />
          </RequireAuth>
        ),
      },
      {
        // Seller-rejected / admin-cancelled paid items, refunded by hand.
        path: 'refunds',
        element: (
          <RequireAuth scopes={['payout.read']}>
            <RefundsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'error-logs',
        element: (
          <RequireAuth scopes={['audit.read']}>
            <ErrorLogsPage />
          </RequireAuth>
        ),
      },
      {
        // Every SMS / email / push attempt, with the gateway's answer. 90 days.
        path: 'communication-logs',
        element: (
          <RequireAuth scopes={['audit.read']}>
            <CommunicationLogsPage />
          </RequireAuth>
        ),
      },
      {
        path: 'audit-trail',
        element: (
          <RequireAuth scopes={['audit.read']}>
            <AuditTrailPage />
          </RequireAuth>
        ),
      },
      {
        // Platform settings live on one page. `/config` used to render a second,
        // mock-only screen whose delivery radius and per-category commission
        // rates persisted nothing and contradicted the real values — the link is
        // kept so existing bookmarks land somewhere sensible.
        path: 'config',
        element: <Navigate to="/integrations" replace />,
      },
      {
        path: 'integrations',
        element: (
          <RequireAuth scopes={['config.write']}>
            <IntegrationsPage />
          </RequireAuth>
        ),
      },
      {
        // DLT template tracking + MSG91 ids. Same scope as the SMS gateway
        // credentials on Platform Config.
        path: 'sms-templates',
        element: (
          <RequireAuth scopes={['config.write']}>
            <SmsTemplatesPage />
          </RequireAuth>
        ),
      },
      {
        // Help & support details, privacy policy and terms per app. Same scope
        // as Platform config — this is platform-wide copy, not catalog content.
        path: 'app-content',
        element: (
          <RequireAuth scopes={['config.write']}>
            <AppContentPage />
          </RequireAuth>
        ),
      },
      {
        path: 'admins',
        element: (
          <RequireAuth roles={['SUPER_ADMIN']}>
            <AdminsPage />
          </RequireAuth>
        ),
      },
      { path: 'settings', element: <SettingsPage /> },
      { path: '2fa-setup', element: <TwoFactorSetupPage /> },
    ],
  },
]);
